import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  fetchUserData,
  updateUserData,
  fetchUserByUsername,
} from '../firebase/User';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const loadUserData = useCallback(() => {
    if (!currentUser) {
      setUserData(null);
      setLoading(false);
      return null;
    }
    return fetchUserData(
      currentUser.uid,
      (data) => {
        setUserData(data);
        setLoading(false);
      },
      // A listener error is terminal (Firestore will not retry it). Resolve the
      // loading state anyway, or AuthGate spins forever. userData is left as-is
      // so a previously-delivered doc survives; if none ever arrived it stays
      // null and AuthGate routes to UserDocRecovery, which surfaces a real error.
      () => setLoading(false),
    );
  }, [currentUser]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = loadUserData();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [loadUserData]);

  const handleUpdateUserData = useCallback(async (updates) => {
    if (!currentUser) return;
    try {
      await updateUserData(currentUser.uid, updates);
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  }, [currentUser]);

  const getUserByUsername = useCallback(async (username) => {
    try {
      return await fetchUserByUsername(username);
    } catch (error) {
      console.error('Error fetching user by username:', error);
      return null;
    }
  }, []);

  const getCurrentUserId = useCallback(() => currentUser?.uid, [currentUser]);

  const value = {
    userData,
    loading,
    updateUserData: handleUpdateUserData,
    getUserByUsername,
    getCurrentUserId,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
